import * as THREE from "three";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader";
// @ts-ignore - JS module without types
import { SceneMaterialSwitcher } from "../color-engine/dioxygen/three-js-material-switcher.module.js";
// @ts-ignore - JS module without types and named exports
import {
  RenderPass,
  ShaderPass,
  PingPongShaderPass,
  AverageShader,
  BlendShader,
  BlendMode,
  EffectComposer
} from "../color-engine/dioxygen/three-js-post-processing-pipeline.module.js";
// @ts-ignore - JS module without types
import {
  MeshNormalDepthMaterial,
  MeshViewPositionMaterial
} from "../color-engine/dioxygen/three-js-extra-public.module.js";
// @ts-ignore - JS module without types
import { ABAOMaterial } from "../color-engine/dioxygen/three-js-shaders.ao.js";
// @ts-ignore - JS module without types
import { BilateralBlurShader } from "../color-engine/dioxygen/three-js-shaders.blur.js";
// @ts-ignore - JS module without types
import { CurvatureMaterial } from "../color-engine/dioxygen/three-js-shaders.geometry.js";
import RenderingPipeline from "../rendering/RenderingPipeline.js";
import { SceneManager } from "../three/SceneManager.js";
import { ControllerManager } from "../three/index.js";

interface RenderOptions {
  scene: THREE.Scene;
  camera: THREE.Camera;
  renderer: THREE.WebGLRenderer;
  ssaaLevelMin: number;
  ssaaLevelMax: number;
}

export class Ao extends RenderingPipeline {
  private static instance: Ao;
  declare sceneManager: SceneManager;
  declare composer: typeof EffectComposer;
  declare controller: ControllerManager;
  materialSwitcher!: SceneMaterialSwitcher;
  renderOptions: RenderOptions | null = null;
  renderPass: typeof RenderPass;
  positionPass: typeof RenderPass;
  normalPass: typeof RenderPass;
  gaussianPass: typeof PingPongShaderPass;
  mipPass1: typeof ShaderPass;
  mipPass2: typeof ShaderPass;
  mipPass3: typeof ShaderPass;
  mipPass4: typeof ShaderPass;
  mipPass5: typeof ShaderPass;
  ABAOPrPass: typeof ShaderPass;
  curvaturePass: typeof ShaderPass;
  cropPass: typeof ShaderPass;
  blendPass: typeof ShaderPass;
  blendCurvaturePass: typeof ShaderPass;
  fxaaPass: typeof ShaderPass;
  abao_interp_fac: number = 1.0;
  scene_AABB_radius: number = 321.5;
  step_sub_factor: number = 125.0;
  ao_contrast: number = 1.4;
  nb_step: number = 4;
  direction_nb: number = 6;
  aoEnabled: boolean = true;
  ssaaEnabled: boolean = false;
  initialize: boolean = false;
  normalDepthMaterial: MeshNormalDepthMaterial = new MeshNormalDepthMaterial();
  viewPositionMaterial: MeshViewPositionMaterial =
    new MeshViewPositionMaterial();

  renderNeedsUpdate: boolean = false;

  static getInstance(): Ao {
    if (!Ao.instance) {
      Ao.instance = new Ao();
    }
    return Ao.instance;
  }

  setProperties(
    aoEnabled: boolean,
    isMobile: boolean,
    ssaa: boolean,
    sceneManager: SceneManager,
    composer: typeof EffectComposer,
    controller: ControllerManager
  ) {
    this.aoEnabled = aoEnabled;
    this.sceneManager = sceneManager;
    this.composer = composer;
    this.controller = controller;
    if (!isMobile) {
      this.ssaaEnabled = ssaa; // no SSAA if mobile
    }
  }

  init = (): void => {
    if (this.sceneManager) {
      const { scene, camera, renderer } = this.sceneManager;
      if (!this.materialSwitcher && scene) {
        this.materialSwitcher = new SceneMaterialSwitcher(scene);
      }

      if (scene && camera && renderer) {
        this.setAoPass(scene, camera, renderer);
        this.initialize = true;
      }
    }
  };

  setRenderPassLazyRendering = (pass: any): void => {
    const self = this;
    pass.onBeforeRender = () => {
      if (self.controller.getRenderNeedsUpdate()) {
        pass.hasChanged();
      }
    };

    pass.onRendered = () => {
      self.controller.setRenderNeedsUpdate(
        pass.isSSAAActive() && !pass.isFinalRenderDone()
      );
    };
  };

  setAoPass = (
    inScene: THREE.Scene,
    inCamera: THREE.Camera,
    inRenderer: THREE.WebGLRenderer
  ): void => {
    if (this.renderPass) this.composer.removePass(this.renderPass);
    this.renderOptions = {
      scene: inScene,
      camera: inCamera,
      renderer: inRenderer,
      ssaaLevelMin: this.ssaaEnabled ? 0 : 0,
      ssaaLevelMax: this.ssaaEnabled ? 4 : 0
    };
    this.renderPass = new RenderPass({
      ...this.renderOptions,
      dependTextures: {},
      targets: ["rendered_scene"]
    });
    const positionRtParams = {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      type: THREE.FloatType
    };
    this.setRenderPassLazyRendering(this.renderPass);
    if (this.positionPass) this.composer.removePass(this.positionPass);
    this.positionPass = new RenderPass({
      ...this.renderOptions,
      dependTextures: {},
      targets: [{ position: positionRtParams }],
      overrideMaterial: "position",
      materialSwitcher: this.materialSwitcher,
      clearColor: new THREE.Color(0, 0, 0),
      clearAlpha: 0.0
    });

    // Render normal buffer
    if (this.normalPass) this.composer.removePass(this.normalPass);
    this.normalPass = new RenderPass({
      ...this.renderOptions,
      dependTextures: {},
      targets: ["normalDepth"],
      overrideMaterial: "normalDepth",
      materialSwitcher: this.materialSwitcher,
      clearColor: new THREE.Color(0, 0, 0),
      clearAlpha: 0.0
    });

    // Set up ABAO Pass
    this.abao_interp_fac = 1.0;
    if (this.ABAOPrPass) this.composer.removePass(this.ABAOPrPass);

    this.ABAOPrPass = new ShaderPass({
      shaderMat: new ABAOMaterial({
        mode: "MaxOcclusion",
        customMipmaps: true
      }),
      renderer: inRenderer,
      dependTextures: {
        normalTex: "normalDepth",
        positionTex: "position",
        posMipmap1Tex: "mip1",
        posMipmap2Tex: "mip2",
        posMipmap3Tex: "mip3",
        posMipmap4Tex: "mip4",
        posMipmap5Tex: "mip5"
      },
      targets: ["ABAO"],
      clearColor: new THREE.Color(1, 1, 1),
      clearAlpha: 1.0
    });
    // Uniform values to set up, should work for any kind of scene

    this.ABAOPrPass.setUniformValue("max_R", this.scene_AABB_radius / 10.0); // /2.0 should be the correct value
    this.ABAOPrPass.setUniformValue(
      "step_size",
      this.scene_AABB_radius / this.step_sub_factor
    );
    this.ABAOPrPass.setUniformValue("ao_contrast", this.ao_contrast);
    this.ABAOPrPass.setUniformValue("nb_step", this.nb_step);
    this.ABAOPrPass.setUniformValue("direction_nb", this.direction_nb);
    this.ABAOPrPass.setUniformValue("proj_m", inCamera.projectionMatrix); // important do not forget

    // Blur the result
    if (this.gaussianPass) this.composer.removePass(this.gaussianPass);
    this.gaussianPass = new PingPongShaderPass({
      shaderMat: BilateralBlurShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "ABAO" },
      targets: ["Gaussian_post"],
      pingPongUniforms: { Gaussian_post: "pingPongTexture" },
      iterationNumber: 4
    });
    this.gaussianPass.setUniformValue("sigma", 1.0);
    this.gaussianPass.setUniformValue("sigma_bl", 0.25);
    this.gaussianPass.setUniformValue("radius", 3);

    // Add position custom mipmaps computation that are mendatory for ABAO

    const mip_rt_params = {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      type: THREE.FloatType
    };
    if (this.mipPass1) this.composer.removePass(this.mipPass1);
    if (this.mipPass2) this.composer.removePass(this.mipPass2);
    if (this.mipPass3) this.composer.removePass(this.mipPass3);
    if (this.mipPass4) this.composer.removePass(this.mipPass4);
    if (this.mipPass5) this.composer.removePass(this.mipPass5);
    this.mipPass1 = new ShaderPass({
      shaderMat: AverageShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "position" },
      targets: [{ mip1: mip_rt_params }]
    });
    this.mipPass1.setResolutionScale(0.5);

    this.mipPass2 = new ShaderPass({
      shaderMat: AverageShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "mip1" },
      targets: [{ mip2: mip_rt_params }]
    });
    this.mipPass2.setResolutionScale(0.25);

    this.mipPass3 = new ShaderPass({
      shaderMat: AverageShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "mip2" },
      targets: [{ mip3: mip_rt_params }]
    });
    this.mipPass3.setResolutionScale(0.125);

    this.mipPass4 = new ShaderPass({
      shaderMat: AverageShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "mip3" },
      targets: [{ mip4: mip_rt_params }]
    });
    this.mipPass4.setResolutionScale(0.065);

    this.mipPass5 = new ShaderPass({
      shaderMat: AverageShader,
      renderer: inRenderer,
      dependTextures: { inTexture: "mip4" },
      targets: [{ mip5: mip_rt_params }]
    });
    this.mipPass5.setResolutionScale(0.0325);

    // Crop outside position buffer -> useful if using a lower resolution version of ABAO
    if (this.cropPass) this.composer.removePass(this.cropPass);
    this.cropPass = new ShaderPass({
      shaderMat: BlendShader,
      renderer: inRenderer,
      dependTextures: { inTexture_1: "Gaussian_post", inTexture_2: "position" },
      targets: ["Crop"]
    });
    this.cropPass.setUniformValue("mode", BlendMode.AlphaMultiply);

    // Blend rendering and ABAO
    if (this.blendPass) this.composer.removePass(this.blendPass);
    this.blendPass = new ShaderPass({
      shaderMat: BlendShader,
      renderer: inRenderer,
      dependTextures: { inTexture_1: "rendered_scene", inTexture_2: "Crop" },
      targets: ["Blend"]
    });
    this.blendPass.setUniformValue("mode", BlendMode.LuminanceMultiply);
    this.blendPass.setUniformValue("factor", 1.0);

    // Compute image-space curvature
    if (this.curvaturePass) this.composer.removePass(this.curvaturePass);
    this.curvaturePass = new ShaderPass({
      shaderMat: new CurvatureMaterial(),
      renderer: inRenderer,
      dependTextures: { normalDepthTex: "normalDepth" },
      targets: ["Curvature"],
      clearColor: new THREE.Color(1, 1, 1),
      clearAlpha: 0.0
    });
    this.curvaturePass.setUniformValue("curvatureScale", 6.0);
    this.curvaturePass.setUniformValue("curvatureMode", 0);

    if (this.blendCurvaturePass)
      this.composer.removePass(this.blendCurvaturePass);
    this.blendCurvaturePass = new ShaderPass({
      shaderMat: BlendShader,
      renderer: inRenderer,
      dependTextures: { inTexture_1: "Blend", inTexture_2: "Curvature" },
      targets: ["BlendCurvature"]
    });
    this.blendCurvaturePass.setUniformValue("mode", BlendMode.OpaqueMultiply);
    this.blendCurvaturePass.setUniformValue("factor", 0.3);

    // Apply post processing anti aliasing
    if (this.fxaaPass) this.composer.removePass(this.fxaaPass);
    this.fxaaPass = new ShaderPass({
      renderer: inRenderer,
      shaderMat: FXAAShader,
      dependTextures: { tDiffuse: "BlendCurvature" },
      targets: ["AA"]
    });
    const pixelRatio = inRenderer.getPixelRatio();
    const size = new THREE.Vector2();

    // TODO should be updated when resized
    inRenderer.getSize(size);
    this.fxaaPass.material.uniforms.resolution.value.x =
      1 / (size.x * pixelRatio);
    this.fxaaPass.material.uniforms.resolution.value.y =
      1 / (size.y * pixelRatio);

    this.setUpPipeline({ aoEnabled: this.aoEnabled, curvatureEnabled: true });
    this.composer._checkPassesConnections();
  };

  setUpPipeline = (params: {
    aoEnabled: boolean;
    curvatureEnabled: boolean;
  }): void => {
    this.composer.clear();
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.fxaaPass);
    if (params.curvatureEnabled) {
      this.composer.addPass(this.curvaturePass);
      this.composer.addPass(this.normalPass);
      this.composer.addPass(this.blendCurvaturePass);
    }
    if (params.aoEnabled) {
      this.composer.addPass(this.mipPass1);
      this.composer.addPass(this.mipPass2);
      this.composer.addPass(this.mipPass3);
      this.composer.addPass(this.mipPass4);
      this.composer.addPass(this.mipPass5);
      this.composer.addPass(this.positionPass);
      this.composer.addPass(this.ABAOPrPass);
      this.composer.addPass(this.gaussianPass);
      this.composer.addPass(this.cropPass);
      this.composer.addPass(this.blendPass);
    }
    // if ao disabled standard render and FXAA only
    if (params.curvatureEnabled) {
      this.blendCurvaturePass.changeDependTexture(
        "inTexture_1",
        params.aoEnabled ? "Blend" : "rendered_scene"
      );
    }
    this.fxaaPass.changeDependTexture(
      "tDiffuse",
      params.aoEnabled ? "BlendCurvature" : "rendered_scene"
    );
    this.setRenderNeedsUpdate(true);
  };

  setMaterialCorrespondance = (child: THREE.Mesh) => {
    this.materialSwitcher.setMaterialCorrespondance(
      child.material,
      "normalDepth",
      this.normalDepthMaterial,
      true
    );
    this.materialSwitcher.setMaterialCorrespondance(
      child.material,
      "position",
      this.viewPositionMaterial,
      true
    );
  };

  // To call when renderer is resized
  updateSize = (renderer: THREE.WebGLRenderer): void => {
    if (this.fxaaPass) {
      const pixelRatio = renderer.getPixelRatio();
      const size = new THREE.Vector2();
      renderer.getSize(size);
      this.fxaaPass.material.uniforms.resolution.value.x =
        1 / (size.x * pixelRatio);
      this.fxaaPass.material.uniforms.resolution.value.y =
        1 / (size.y * pixelRatio);
    }
  };

  setAoEnabled = (aoEnabled: boolean): void => {
    this.aoEnabled = aoEnabled;
    this.setUpPipeline({ aoEnabled: aoEnabled, curvatureEnabled: true });
   // this.composer._checkPassesConnections();
    //this.sceneUpdated();
  };

  render = (): void => {
    if (this.composer) {
      this.composer.render();
    }
  };

  approxRollingAverage = (
    average: number,
    value: number,
    history: number = 50
  ): number => {
    average -= average / history;
    average += value / history;

    return average;
  };

  setScene = (scene: THREE.Scene): void => {
    // TODO to change when refactoring app (setScene should be called but cannot work with the current pipeline)
    this.materialSwitcher.setScene(scene, false);
    const self = this;
    // this.setAoPass(scene, camera, renderer);
    if (this.renderPass) {
      this.renderPass.setScene(scene);
    }
    if (this.normalPass) {
      this.normalPass.setScene(scene);

      // Hide objects that do not have normalDepth material before rendering the pass
      this.normalPass.onBeforeRender = () => {
        if (self.controller.getRenderNeedsUpdate()) {
          self.normalPass.hasChanged();
        }

        scene.traverse((obj) => {
          if (
            obj instanceof THREE.Mesh &&
            !this.materialSwitcher.hasCorrespondingMaterial(
              obj.material,
              "normalDepth"
            )
          ) {
            obj.visible = false;
          }
        });
      };

      // Show objects that do not have normalDepth material after rendering the pass
      this.normalPass.onRendered = () => {
        self.renderNeedsUpdate =
          self.normalPass.isSSAAActive() &&
          !self.normalPass.isFinalRenderDone();
        scene.traverse((obj) => {
          if (
            obj instanceof THREE.Mesh &&
            !this.materialSwitcher.hasCorrespondingMaterial(
              obj.material,
              "normalDepth"
            )
          ) {
            obj.visible = true;
          }
        });
      };
    }
    if (this.positionPass) {
      this.positionPass.setScene(scene);

      // Hide objects that do not have position material before rendering the pass
      this.positionPass.onBeforeRender = () => {
        if (self.controller.getRenderNeedsUpdate()) {
          self.positionPass.hasChanged();
        }
        scene.traverse((obj) => {
          if (
            obj instanceof THREE.Mesh &&
            !this.materialSwitcher.hasCorrespondingMaterial(
              obj.material,
              "position"
            )
          ) {
            obj.visible = false;
          }
        });
      };

      // Show objects that do not have position material after rendering the pass
      this.positionPass.onRendered = () => {
        self.renderNeedsUpdate =
          self.positionPass.isSSAAActive() &&
          !self.positionPass.isFinalRenderDone();
        scene.traverse((obj) => {
          if (
            obj instanceof THREE.Mesh &&
            !this.materialSwitcher.hasCorrespondingMaterial(
              obj.material,
              "position"
            )
          ) {
            obj.visible = true;
          }
        });
      };
    }
    this.sceneUpdated();
  };

  setRenderNeedsUpdate = (renderNeedsUpdate: boolean): void => {
    this.renderNeedsUpdate = renderNeedsUpdate;
    if (renderNeedsUpdate) {
      if (this.renderPass) {
        this.renderPass.hasChanged();
      }
      if (this.normalPass) {
        this.normalPass.hasChanged();
      }
      if (this.positionPass) {
        this.positionPass.hasChanged();
      }
    }
  };

  sceneUpdated = (): void => {
    this.materialSwitcher.saveOriginalMaterials();
    this.setRenderNeedsUpdate(true);
  };

  reset = (): void => {
    //this.materialSwitcher.saveOriginalMaterials();
  };
}
